import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Modal,
  Button,
  TextField,
  Select,
  TextDescription,
  Checkbox,
  Typography,
} from "@ann/web-components";
import {
  ModalContent,
  FormField,
  ExpireContainer,
  ExpireValueInput,
  ExpireUnitSelect,
  NeverExpiresWrapper,
} from "./CreateFactModal.styled";
import { useMediaQueries } from "../../../../../../../utils/mediaQueries";
import { createFact } from "../../../../services";
import { useToast } from "../../../../../../../components/toast";

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "PENDING", label: "Pending" },
];

const SOURCE_TYPE_OPTIONS = [
  { value: "USER", label: "User" },
  { value: "AGENT", label: "Train KB" },
  { value: "DOCUMENT", label: "Direct Document" },
];

const EXPIRE_UNIT_OPTIONS = [
  { value: "DAYS", label: "Days" },
  { value: "WEEKS", label: "Weeks" },
  { value: "MONTHS", label: "Months" },
  { value: "YEARS", label: "Years" },
];

const EXPIRE_VALUE_OPTIONS = [
  { value: 30, label: "30" },
  { value: 60, label: "60" },
  { value: 90, label: "90" },
  { value: 180, label: "180" },
  { value: 365, label: "365" },
];

const CreateFactModal = ({ isOpen, onClose, knowledgeBase, onCreate, knowledgeBaseUuid, knowledgeBases = [] }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    title: "",
    fact: "",
    status: "DRAFT",
    sourceType: "USER",
    selectedKnowledgeBaseUuid: knowledgeBaseUuid || "",
    expireValue: 90,
    expireUnit: "DAYS",
    neverExpires: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { isMobile, isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();

  const modalWidth = useMemo(() => {
    if (isMobile || isIpadAirPortrait || isIpadProPortrait) {
      return "95%";
    }
    return "500px";
  }, [isMobile, isIpadAirPortrait, isIpadProPortrait]);

  // Build knowledge bases options for Select
  const knowledgeBaseOptions = useMemo(() => {
    if (!knowledgeBases || knowledgeBases.length === 0) {
      // If no list provided, use current knowledgeBase as single option
      if (knowledgeBase?.name && knowledgeBaseUuid) {
        return [
          {
            value: knowledgeBaseUuid,
            label: knowledgeBase.name,
          },
        ];
      }
      return [];
    }
    return knowledgeBases.map((kb) => ({
      value: kb.id || kb.uuid || kb.knowledgebase_uuid || kb.knowledgebaseUuid,
      label: kb.name,
    }));
  }, [knowledgeBases, knowledgeBase, knowledgeBaseUuid]);

  // Use selectedKnowledgeBaseUuid from formData, fallback to knowledgeBaseUuid prop
  const resolvedKnowledgeBaseUuid = formData.selectedKnowledgeBaseUuid || knowledgeBaseUuid || knowledgeBase?.uuid;

  // Sync selectedKnowledgeBaseUuid when knowledgeBaseUuid prop changes
  useEffect(() => {
    if (knowledgeBaseUuid && !formData.selectedKnowledgeBaseUuid) {
      setFormData((prev) => ({
        ...prev,
        selectedKnowledgeBaseUuid: knowledgeBaseUuid,
      }));
    }
  }, [knowledgeBaseUuid, formData.selectedKnowledgeBaseUuid]);

  const handleFieldChange = useCallback(
    (field) => (value) => {
      setFormData((prev) => {
        // Handle expireValue as number when coming from Select
        const processedValue = field === "expireValue" ? Number(value) : value;
        return {
          ...prev,
          [field]: processedValue,
        };
      });
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: null,
        }));
      }
    },
    [errors]
  );

  const handleNeverExpiresChange = useCallback((checked) => {
    setFormData((prev) => ({
      ...prev,
      neverExpires: checked,
    }));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.selectedKnowledgeBaseUuid) {
      newErrors.selectedKnowledgeBaseUuid = "Knowledge base is required";
    }

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.fact.trim()) {
      newErrors.fact = "Fact is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleCancel = useCallback(() => {
    setFormData({
      title: "",
      fact: "",
      status: "DRAFT",
      sourceType: "USER",
      selectedKnowledgeBaseUuid: knowledgeBaseUuid || "",
      expireValue: 90,
      expireUnit: "DAYS",
      neverExpires: false,
    });
    setErrors({});
    setIsLoading(false);
    onClose();
  }, [onClose, knowledgeBaseUuid]);

  const handleCreate = useCallback(async () => {
    if (!validateForm()) return;
    console.log("resolvedKnowledgeBaseUuid", resolvedKnowledgeBaseUuid);
    // Validate that knowledgebase_uuid from URL is present
    if (!resolvedKnowledgeBaseUuid) {
      toast.error({
        title: "Missing knowledge base",
        message: "knowledgebase_uuid is required to create a fact. Please ensure you're on a valid knowledge base page.",
        canClose: true,
      });
      return;
    }

    const shouldIncludeExpirePolicy = !formData.neverExpires;
    const expireLimit = Number.parseInt(String(formData.expireValue), 10);

    if (shouldIncludeExpirePolicy) {
      if (!Number.isFinite(expireLimit) || expireLimit <= 0) {
        toast.error({
          title: "Invalid expiration",
          message: "Expire limit must be a positive number (or set 'Never expires').",
          canClose: true,
        });
        return;
      }
    }

    // Build payload with knowledgebase_uuid from URL
    const payload = {
      knowledgebase_uuid: resolvedKnowledgeBaseUuid,
      title: formData.title.trim(),
      description: formData.fact.trim(),
      status: formData.status,
      source_type: formData.sourceType,
      ...(shouldIncludeExpirePolicy
        ? { expire_policy: { limit: expireLimit, type: formData.expireUnit } }
        : {}),
      no_expire: Boolean(formData.neverExpires),
    };

    try {
      setIsLoading(true);

      const response = await createFact(payload);

      // Backend returns { status_code, msgCode, body } - getApiResponse normalizes to snake_case
      // Check both statusCode (camelCase) and status_code (snake_case) for compatibility
      const statusCode = response?.statusCode || response?.status_code;
      if (statusCode !== 200) {
        const code = response?.msgCode?.[0]?.code || response?.msg_code?.[0]?.code || "ERROR";
        const description =
          response?.msgCode?.[0]?.description || response?.msg_code?.[0]?.description || "Failed to create fact.";
        toast.error({ title: code, message: description, canClose: true });
        return;
      }

      toast.success({
        title: "Fact created",
        message: response?.msgCode?.[0]?.description || response?.msg_code?.[0]?.description || "Fact created successfully.",
        canClose: true,
      });

      // Check both factUuid (camelCase) and fact_uuid (snake_case) for compatibility
      onCreate?.({
        fact_uuid: response?.body?.factUuid || response?.body?.fact_uuid,
      });

      // Close & reset
      handleCancel();
    } catch (error) {
      const apiErr = error?.response?.data;
      const code = apiErr?.msgCode?.[0]?.code || apiErr?.msg_code?.[0]?.code;
      const description = apiErr?.msgCode?.[0]?.description || apiErr?.msg_code?.[0]?.description;

      toast.error({
        title: code || "Error",
        message: description || "An error occurred while creating the fact.",
        canClose: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [formData, handleCancel, onCreate, resolvedKnowledgeBaseUuid, toast, validateForm]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="medium"
      width={modalWidth}
    >
      <Modal.Header>
        <Modal.Header.Title>Create New Fact</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          <Typography
            category="body"
            size="medium"
            weight="regular"
            color="body"
          >
            Add a new Fact to the Knowledge Base. All fields marked with * are
            required.
          </Typography>

          <FormField>
            <Typography category="label" size="small" weight="bold">
              Knowledge Base *
            </Typography>
            <Select
              value={knowledgeBaseOptions.find(option => option.value === formData.selectedKnowledgeBaseUuid)?.label}
              onChange={handleFieldChange("selectedKnowledgeBaseUuid")}
              size="small"
              state={errors.selectedKnowledgeBaseUuid ? "error" : "default"}
              placeholder="Select a knowledge base"
            >
              <Select.Menu>
                {knowledgeBaseOptions.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  >
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Menu>
            </Select>
            {errors.selectedKnowledgeBaseUuid && (
              <span
                style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px" }}
              >
                {errors.selectedKnowledgeBaseUuid}
              </span>
            )}
          </FormField>

          <FormField>
            <Typography category="label" size="small" weight="bold">
              Title *
            </Typography>
            <TextField
              value={formData.title}
              onChange={handleFieldChange("title")}
              placeholder="e.g., Customer Support Hours"
              size="small"
              state={errors.title ? "error" : "default"}
            />
            {errors.title && (
              <span
                style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px" }}
              >
                {errors.title}
              </span>
            )}
          </FormField>

          <FormField>
            <Typography category="label" size="small" weight="bold">
              Fact *
            </Typography>
            <TextDescription
              label=""
              value={formData.fact}
              onChange={handleFieldChange("fact")}
              placeholder="Enter the fact or information"
              height="120px"
              size="small"
            />
          </FormField>

          <FormField>
            <Typography category="label" size="small" weight="bold">
              Status *
            </Typography>
            <Select
              value={formData.status}
              onChange={handleFieldChange("status")}
              size="small"
            >
              <Select.Menu>
                {STATUS_OPTIONS.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  >
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Menu>
            </Select>
          </FormField>

          <FormField>
            <Typography category="label" size="small" weight="bold">
              Source Type *
            </Typography>
            <Select
              value={formData.sourceType}
              onChange={handleFieldChange("sourceType")}
              size="small"
            >
              <Select.Menu>
                {SOURCE_TYPE_OPTIONS.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  >
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Menu>
            </Select>
          </FormField>
          <FormField>
            <Typography category="label" size="small" weight="bold">
              Expire in
            </Typography>
            <ExpireContainer>
              <ExpireValueInput>
                <Select
                  value={formData.expireValue}
                  onChange={handleFieldChange("expireValue")}
                  size="small"
                  state={formData.neverExpires ? "disabled" : "default"}
                  placeholder="90"
                >
                  <Select.Menu>
                    {EXPIRE_VALUE_OPTIONS.map((option) => (
                      <Select.Item
                        key={option.value}
                        value={option.value}
                        label={option.label}
                      >
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Menu>
                </Select>
              </ExpireValueInput>
              <ExpireUnitSelect>
                <Select
                  value={formData.expireUnit}
                  onChange={handleFieldChange("expireUnit")}
                  size="small"
                  state={formData.neverExpires ? "disabled" : "default"}
                >
                  <Select.Menu>
                    {EXPIRE_UNIT_OPTIONS.map((option) => (
                      <Select.Item
                        key={option.value}
                        value={option.value}
                        label={option.label}
                      >
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Menu>
                </Select>
              </ExpireUnitSelect>
              <NeverExpiresWrapper>
                <Checkbox
                  checked={formData.neverExpires}
                  onChange={handleNeverExpiresChange}
                  size="medium"
                />
                <Typography
                  category="body"
                  size="medium"
                  weight="regular"
                  color="body"
                >
                  Never expires
                </Typography>
              </NeverExpiresWrapper>
            </ExpireContainer>
          </FormField>
        </ModalContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Button
          label="Cancel"
          size="medium"
          type="secondary"
          onClick={handleCancel}
          disabled={isLoading}
        />
        <Button
          label="Create Fact"
          size="medium"
          type="primary"
          onClick={handleCreate}
          disabled={isLoading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default CreateFactModal;
